import PropTypes from "prop-types";
import { useNavigate } from "react-router";

import ExportDatasetButton from "@/components/Export/ExportDatasetButton";
import DeleteButton from "@/components/Header/Button/DeleteButton";
import InfoButton from "@/components/Header/Button/InfoButton";
import ProgressButton from "@/components/Header/Button/ProgressButton";
import HeaderButtonList from "@/components/Header/HeaderButtonList/HeaderButtonList";
import { BUTTON_TYPE, BUTTON_KEYS } from "@/constants/Button";
import { TAB_KEYS, TAB_PARAM_KEYS } from "@/constants/TabKeys";
import Info from "@/features/Interventions/Info";
import InterventionEdit from "@/features/Interventions/InterventionEdit";
import { useAuthorization } from "@/modules/authorization";
import { InterventionDuplicateButton } from "@/modules/intervention/intervention-duplicate";
import { InterventionFollowupDropdown } from "@/modules/intervention/intervention-followup";
import ModelFavoriteButton from "@/modules/model-favorite-button";
import { useSidePanel } from "@/modules/side-panel";
import { StateEditForm } from "@/modules/state-edit-form";
import { useIdParam } from "@/services/router";
import { EditButton } from "@/ui/edit-button";

const HeaderButtons = ({ intervention = {} }) => {
  const { canCreate, canUpdate, canDelete } = useAuthorization();

  const navigate = useNavigate();

  const { openSidePanel } = useSidePanel();
  const id = useIdParam();

  const predicateRemoveKeys = (key) => {
    const queryKey = key.queryKey;
    return (
      queryKey.includes(intervention.id) && queryKey.includes("recommendations")
    );
  };

  const onInfoSelect = () => {
    openSidePanel({
      title: intervention.name,
      component: <Info intervention={intervention} />,
    });
  };

  const onEditClick = () => {
    openSidePanel({
      title: intervention.name,
      component: <InterventionEdit interventionId={intervention.id} />,
    });
  };

  const onDeleted = () => {
    const recommendedBy = intervention.recommended_by;

    const url = recommendedBy
      ? `/interventions/${recommendedBy.id}`
      : `/assets/${intervention.asset_id}?${TAB_PARAM_KEYS.CONTEXT_TAB_KEY}=${TAB_KEYS.ASSETS_INTERVENTIONS}`;

    navigate(url, { replace: true });
  };

  const primaryActions = [];
  if (canUpdate(intervention)) {
    primaryActions.push(
      <StateEditForm
        key={BUTTON_KEYS.STATE_EDIT}
        stateType="intervention"
        disabled={!intervention.intervention_template_id}
        model={intervention}
      />
    );
  }

  primaryActions.push(
    <ProgressButton
      key={BUTTON_KEYS.PROGRESS}
      type="intervention"
      model={intervention.id ? intervention : { ...intervention, id }}
      isEditable={canUpdate(intervention)}
    />
  );

  primaryActions.push(
    <InterventionFollowupDropdown
      key={BUTTON_KEYS.FOLLOW_UP}
      intervention={intervention}
    />
  );

  primaryActions.push(
    <InfoButton key={BUTTON_KEYS.INFO} onInfoSelect={onInfoSelect} />
  );

  primaryActions.push(
    <ModelFavoriteButton
      key={BUTTON_KEYS.FAVORITE}
      modelKey="intervention"
      item={intervention}
    />
  );

  const secondaryActions = [];
  secondaryActions.push({
    key: BUTTON_KEYS.EXPORT,
    label: (
      <ExportDatasetButton
        key={BUTTON_KEYS.EXPORT}
        templateId={intervention.intervention_template_id}
        params={{ "filter[intervention_id]": intervention.id }}
        buttonProps={{
          type: BUTTON_TYPE.TEXT,
        }}
      />
    ),
  });

  if (canUpdate(intervention)) {
    secondaryActions.push({
      key: BUTTON_KEYS.EDIT,
      label: (
        <EditButton key={BUTTON_KEYS.EDIT} type="text" onClick={onEditClick} />
      ),
    });
  }

  if (canCreate("intervention")) {
    secondaryActions.push({
      key: BUTTON_KEYS.DUPLICATE,
      label: (
        <InterventionDuplicateButton
          key={BUTTON_KEYS.DUPLICATE}
          intervention={intervention}
          type="text"
          showIcon
        />
      ),
    });
  }

  if (canDelete(intervention)) {
    secondaryActions.push({
      key: BUTTON_KEYS.DELETE,
      label: (
        <DeleteButton
          key={BUTTON_KEYS.DELETE}
          modelKey="intervention"
          model={intervention}
          onDeleted={onDeleted}
          predicateRemoveKeys={predicateRemoveKeys}
          buttonProps={{
            type: BUTTON_TYPE.TEXT,
          }}
        />
      ),
    });
  }

  return (
    <HeaderButtonList
      primaryActions={primaryActions}
      secondaryActions={secondaryActions}
    />
  );
};

HeaderButtons.propTypes = {
  intervention: PropTypes.object.isRequired,
};

export default HeaderButtons;
